import { useEffect, useState } from "react";

import Container from "react-bootstrap/Container";
import Row from "react-bootstrap/Row";
import Col from "react-bootstrap/Col";
import { toast } from "react-toastify";
import { useSearchParams } from "react-router-dom";

import EmploymentFormModel from "./EmploymentFormModel";
import { EmploymentStatuses, Roles } from "../../utils/constants";
import PrimaryButton from "../../components/PrimaryButton";
import Table from "../../components/Table";
import {
  deleteEmployment,
  getEmployments,
} from "../../services/EmploymentService";
import { ReactComponent as EditIcon } from "../../static/icons/edit.svg";
import { ReactComponent as DeleteIcon } from "../../static/icons/delete-icon.svg";
import useWindowDimensions from "../../utils/hooks/getWindowDimensions";
import SecondaryButton from "../../components/SecondaryButton";
import { getCurrentUser } from "../../utils/utils";

const EmploymentList = () => {
  const [employments, setEmployments] = useState([]);
  const [formOpen, setFormOpen] = useState({
    open: false,
    data: null,
    inviteCode: null,
  });
  const { width } = useWindowDimensions();
  const user = getCurrentUser();
  const [searchParams, setSearchParams] = useSearchParams();

  useEffect(() => {
    if (searchParams.has("invite_code")) {
      setFormOpen({
        data: null,
        open: true,
        inviteCode: searchParams.get("invite_code"),
      });
      setSearchParams(() => {
        searchParams.delete("invite_code");
        return searchParams;
      });
    }
  }, [searchParams]);

  useEffect(() => {
    getEmployments().then((data) => setEmployments(data));
  }, []);

  const getStatusIcon = (status) => {
    const StatusIcon = EmploymentStatuses[status].icon;
    return <StatusIcon />;
  };

  const onDelete = (item) => {
    const deleteCallback = () => {
      toast.success(
        `The ${
          user.role === Roles.EMPLOYER.value ? "employee" : "employer"
        } has been deleted.`
      );
      setEmployments((oldVal) =>
        oldVal.reduce((acc, i) => {
          if (i.id !== item.id) {
            acc.push(i);
          }
          return acc;
        }, [])
      );
    };
    deleteEmployment({ id: item.id }).then(deleteCallback);
  };

  const columns = [
    {
      label: "E-mail or phone number",
      key: "email-phone",
      component: (item) =>
        item?.user
          ? item.user?.email
            ? item.user?.email
            : item.user?.phone_number
          : item?.identifier,
    },
    ...(width > 550
      ? [
          {
            label: "First name",
            key: "first-name",
            name: "user.first_name",
          },
          {
            label: "Last name",
            key: "last-name",
            name: "user.last_name",
          },
        ]
      : []),
    {
      label: "Status",
      key: "status",
      name: "status",
      component: (item) => {
        return (
          <Container>
            {item?.is_private ? "Private" : getStatusIcon(item.status)}
          </Container>
        );
      },
    },
    ...(user.role === Roles.EMPLOYEE.value && width > 550
      ? [
          {
            label: "Hourly Rate",
            key: "hourly-rate",
            name: "hourly_rate",
            component: (item) => {
              return item.hourly_rate
                ? `${item?.hourly_rate} ${item?.hourly_rate_currency}`
                : "N/A";
            },
          },
        ]
      : []),
    {
      hidden: true,
      type: "button",
      key: "edit-btn",
      name: "edit-btn",
      component: (item) =>
        width < 600 ? (
          <EditIcon
            onClick={() =>
              setFormOpen({
                data: item,
                open: true,
              })
            }
          />
        ) : (
          <SecondaryButton
            onClick={() =>
              setFormOpen({
                data: item,
                open: true,
              })
            }
          >
            Edit
          </SecondaryButton>
        ),
    },
    {
      hidden: true,
      type: "button",
      key: "delete-btn",
      name: "delete-btn",
      component: (item) =>
        width < 600 ? (
          <DeleteIcon onClick={() => onDelete(item)} />
        ) : (
          <SecondaryButton onClick={() => onDelete(item)}>
            Delete
          </SecondaryButton>
        ),
    },
  ];

  return (
    <Container fluid>
      <Row>
        <Table
          columns={columns}
          data={employments}
          header={
            <Container style={{ margin: "13px 13px 25px" }}>
              <Row>
                <Col>
                  <h5 className="vertical-align-text table-title">
                    {user.role === Roles.EMPLOYER.value
                      ? "Employees"
                      : "Employers"}
                  </h5>
                </Col>
                <Col>
                  <PrimaryButton
                    onClick={() => setFormOpen({ open: true, data: null })}
                    style={{ float: "right" }}
                  >
                    Add +
                  </PrimaryButton>
                </Col>
              </Row>
            </Container>
          }
        />
      </Row>
      <EmploymentFormModel
        open={formOpen.open}
        onClose={() => setFormOpen({ open: false, data: null })}
        setEmployments={setEmployments}
        employment={formOpen.data}
        inviteCode={formOpen.inviteCode}
      />
    </Container>
  );
};

export default EmploymentList;
